<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const REACTIONS = [
  { id: 'disappointed', emoji: '😞' },
  { id: 'neutral', emoji: '😐' },
  { id: 'happy', emoji: '😃' },
];

const props = defineProps({
  reactions: {
    type: Object,
    default: () => ({
      disappointed: 0,
      neutral: 0,
      happy: 0,
      total: 0,
    }),
  },
  variant: {
    type: String,
    default: 'compact',
    validator: value => ['compact', 'detailed'].includes(value),
  },
});

const { t } = useI18n();

const total = computed(() => props.reactions?.total || 0);

const helpfulPercent = computed(() => {
  if (!total.value) return 0;
  return Math.round((props.reactions.happy / total.value) * 100);
});

const reactionRows = computed(() =>
  REACTIONS.map(reaction => ({
    ...reaction,
    count: props.reactions?.[reaction.id] || 0,
    percent: total.value
      ? Math.round((props.reactions[reaction.id] / total.value) * 100)
      : 0,
  }))
);
</script>

<template>
  <div v-if="total > 0" class="flex flex-col gap-2">
    <div
      v-if="variant === 'compact'"
      class="inline-flex items-center gap-2 text-n-slate-11 whitespace-nowrap"
      :title="
        t('HELP_CENTER.ARTICLES_PAGE.ARTICLE_CARD.CARD.REACTIONS_HELPFUL', {
          percent: helpfulPercent,
        })
      "
    >
      <span
        v-for="reaction in reactionRows"
        v-show="reaction.count > 0"
        :key="reaction.id"
        class="inline-flex items-center gap-0.5 text-sm"
      >
        <span class="text-base leading-none">{{ reaction.emoji }}</span>
        <span>{{ reaction.count }}</span>
      </span>
      <span class="text-sm text-n-slate-10">
        {{
          t('HELP_CENTER.ARTICLES_PAGE.ARTICLE_CARD.CARD.REACTIONS', {
            count: total,
          })
        }}
      </span>
    </div>

    <div v-else class="flex flex-col gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2.5">
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            t('HELP_CENTER.EDIT_ARTICLE_PAGE.ARTICLE_REACTIONS.TITLE')
          }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{
            t('HELP_CENTER.ARTICLES_PAGE.ARTICLE_CARD.CARD.REACTIONS', {
              count: total,
            })
          }}
        </span>
      </div>
      <p class="m-0 text-xs text-n-slate-11">
        {{
          t('HELP_CENTER.ARTICLES_PAGE.ARTICLE_CARD.CARD.REACTIONS_HELPFUL', {
            percent: helpfulPercent,
          })
        }}
      </p>
      <ul class="m-0 flex list-none flex-col gap-1.5 p-0">
        <li
          v-for="reaction in reactionRows"
          :key="reaction.id"
          class="flex items-center justify-between gap-3 text-sm text-n-slate-11"
        >
          <span class="inline-flex items-center gap-1.5">
            <span class="text-base leading-none">{{ reaction.emoji }}</span>
            <span>
              {{
                t(
                  `HELP_CENTER.EDIT_ARTICLE_PAGE.ARTICLE_REACTIONS.${reaction.id.toUpperCase()}`
                )
              }}
            </span>
          </span>
          <span class="tabular-nums text-n-slate-12">
            {{ reaction.count }}
            <span class="text-n-slate-10">({{ reaction.percent }}%)</span>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>
